import '@/styles/globals.css';
import '@fortawesome/fontawesome-svg-core/styles.css';
import '@/styles/phoneInputStyles.css';

import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { NextPage } from 'next';
import type { AppProps } from 'next/app';
import { Inter, Work_Sans } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { type ReactElement, type ReactNode } from 'react';
import ReactGA from 'react-ga4';
import toast, { ToastBar, Toaster } from 'react-hot-toast';

import AuthOnlyPage from '@/components/AuthOnlyPage';
import MindPalChatWidget from '@/components/MindPalChatWidget';
import { isDevelopment } from '@/config/config';
import queryClient from '@/config/queryClient';
import { toastConfig } from '@/config/toastConfig';
import { useCatchUtmParams } from '@/hooks/useCatchUtmParams';
import { useClarity } from '@/hooks/useClarity';
import { useSentryEmailParam } from '@/hooks/useSentryEmailParam';
import { createMetaTags, JsonLd } from '@/utils/seo';

const inter = Inter({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-inter',
});
const workSans = Work_Sans({
  subsets: ['latin'],
  display: 'swap',
  variable: '--font-work-sans',
});

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement, pageProps: P) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
const CLARITY_PROJECT_ID = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;
const DOMAIN = isDevelopment ? 'http://localhost:3000' : 'https://mindpal.co';

export default function App({ Component, pageProps }: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page);
  const { asPath } = useRouter();
  const canonicalUrl = `${DOMAIN}${asPath.split('?')[0]}`;
  const isPdfRender = asPath.startsWith('/pdf-render');
  if (GA_MEASUREMENT_ID) ReactGA.initialize(GA_MEASUREMENT_ID);
  useClarity(CLARITY_PROJECT_ID);
  useSentryEmailParam();
  useCatchUtmParams();

  return (
    <QueryClientProvider client={queryClient}>
      <style
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
        :root {
          --font-work-sans: ${workSans.style.fontFamily};
          --font-inter: ${inter.style.fontFamily};
        }`,
        }}
      />
      <GoogleOAuthProvider
        clientId={`${process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID}`}
      >
        <div className="flex min-h-screen flex-col text-white caret-secondary-500">
          <Head>
            {createMetaTags({
              title: 'MindPal',
              description:
                'Find top FAANG job opportunities on our job board. Connect with leading tech employers and elevate your career with exclusive listings today',
              ogImage: `${DOMAIN}/opengraph-image.png`,
            })}
            <link rel="canonical" href={canonicalUrl} key="canonical" />
            <meta name="theme-color" content="#000000" />
            <JsonLd
              data={{
                '@context': 'https://schema.org',
                '@graph': [
                  {
                    '@type': 'Organization',
                    '@id': 'https://mindpal.co/#organization',
                    name: 'MindPal',
                    url: 'https://mindpal.co',
                    logo: {
                      '@type': 'ImageObject',
                      url: 'https://mindpal.co/opengraph-image.png',
                    },
                    description:
                      'AI-powered CV/resume building and job matching platform',
                    sameAs: ['https://www.linkedin.com/company/mindpal-co'],
                  },
                  {
                    '@type': 'WebSite',
                    '@id': 'https://mindpal.co/#website',
                    url: 'https://mindpal.co',
                    name: 'MindPal',
                    publisher: { '@id': 'https://mindpal.co/#organization' },
                  },
                ],
              }}
            />
          </Head>
          <AuthOnlyPage>
            {getLayout(<Component {...pageProps} />, pageProps)}
          </AuthOnlyPage>
        </div>
        <Toaster
          pauseOnFocusLoss
          toastOptions={toastConfig}
          containerClassName="!top-0 !bottom-0 !left-0 !right-0 justify-normal"
          shouldStack
        >
          {(t) => (
            <ToastBar toast={t}>
              {({ icon, message }) => (
                <>
                  <div />
                  <div className="flex w-fit gap-2  text-center">
                    {icon}
                    {message}
                  </div>
                  {t.type !== 'loading' && (
                    <div className="flex items-center justify-end">
                      <button
                        className="w-fit p-2"
                        type="button"
                        onClick={() => toast.dismiss(t.id)}
                      >
                        <FontAwesomeIcon
                          icon={faXmark}
                          className="text-neutral-700"
                        />
                      </button>
                    </div>
                  )}
                </>
              )}
            </ToastBar>
          )}
        </Toaster>
        <ReactQueryDevtools initialIsOpen={false} />
        <MindPalChatWidget />
      </GoogleOAuthProvider>
      {!isPdfRender && (
        <>
          <Script strategy="afterInteractive" id="clickio-gtag">
            {`
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('consent', 'default', {
        'ad_storage': 'granted',
        'analytics_storage': 'granted',
        'functionality_storage': 'granted',
        'personalization_storage': 'granted',
        'security_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'wait_for_update': 1500
    });
    gtag('consent', 'default', {
        'region': ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IE', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'GB', 'CH'],
        'ad_storage': 'denied',
        'analytics_storage': 'denied',
        'functionality_storage': 'denied',
        'personalization_storage': 'denied',
        'security_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'wait_for_update': 1500
    });
    gtag('set', 'ads_data_redaction', true);
    gtag('set', 'url_passthrough', false);
    (function(){
        const s={adStorage:{storageName:"ad_storage",serialNumber:0},analyticsStorage:{storageName:"analytics_storage",serialNumber:1},functionalityStorage:{storageName:"functionality_storage",serialNumber:2},personalizationStorage:{storageName:"personalization_storage",serialNumber:3},securityStorage:{storageName:"security_storage",serialNumber:4},adUserData:{storageName:"ad_user_data",serialNumber:5},adPersonalization:{storageName:"ad_personalization",serialNumber:6}};let c=localStorage.getItem("__lxG__consent__v2");if(c){c=JSON.parse(c);if(c&&c.cls_val)c=c.cls_val;if(c)c=c.split("|");if(c&&c.length&&typeof c[14]!==undefined){c=c[14].split("").map(e=>e-0);if(c.length){let t={};Object.values(s).sort((e,t)=>e.serialNumber-t.serialNumber).forEach(e=>{t[e.storageName]=c[e.serialNumber]?"granted":"denied"});gtag("consent","update",t)}}}
        if(Math.random() < 0.05) {if (window.dataLayer && (window.dataLayer.some(e => e[0] === 'js' && e[1] instanceof Date) || window.dataLayer.some(e => e['event'] === 'gtm.js' && e['gtm.start'] == true ))) {document.head.appendChild(document.createElement('img')).src = "//clickiocdn.com/utr/gtag/?sid=241372";}}
    })();`}
          </Script>
          <Script
            strategy="afterInteractive"
            src="//clickiocmp.com/t/consent_241372.js"
          />
        </>
      )}
    </QueryClientProvider>
  );
}
